import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2, Clock, AlertCircle } from "lucide-react";

export default function DomainDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/domains/${id}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [id]);

  if (d === null) return <div className="text-sm ink-soft">Loading…</div>;
  if (d === false) return <div className="text-sm ink-soft">Domain not found</div>;

  const StatusIcon = d.status === "verified" ? CheckCircle2 : d.status === "failed" ? AlertCircle : Clock;
  const chipClass = d.status === "verified" ? "chip-success" : d.status === "failed" ? "chip-danger" : "chip-warning";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="domain-detail">
      <Link to="/dashboard/domains" className="inline-flex items-center gap-2 text-sm ink-soft hover:ink">
        <ArrowLeft className="h-4 w-4" /> back to domains
      </Link>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold ink tracking-tight">{d.domain}</h1>
          <p className="mt-1 ink-soft text-sm">Domain trust telemetry and policy state.</p>
        </div>
        <span className={`chip ${chipClass}`}>
          <StatusIcon className="h-3 w-3" /> {d.status}
        </span>
      </div>

      {d.status === "verified" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Metric label="Trust" value={d.trust_score} />
            <Metric label="DMARC" value={`${d.dmarc_compliance}%`} />
            <Metric label="SPF" value={`${d.spf_health}%`} />
            <Metric label="DKIM" value={`${d.dkim_alignment}%`} />
            <Metric label="Threats" value={d.active_threats} danger />
            <Metric label="Senders" value={d.monitored_senders} />
          </div>

          <div className="panel p-6">
            <h3 className="font-display text-lg font-bold ink">Current DMARC policy</h3>
            <pre className="mt-3 font-mono text-xs text-stone-300 bg-[#0F0E0C] rounded-lg p-4 overflow-x-auto">
{`v=DMARC1; p=${d.dmarc_policy}; rua=mailto:reports@trustdmarc.io; pct=100; sp=${d.dmarc_policy}; aspf=r; adkim=r;`}
            </pre>
            <p className="mt-4 ink-soft text-sm">
              Enforcement readiness: <span className="font-semibold ink">{d.enforcement_readiness}%</span> — {d.enforcement_readiness > 80 ? "ready to tighten." : "continue monitoring."}
            </p>
          </div>
        </>
      ) : (
        <div className="panel p-6">
          <h3 className="font-display text-lg font-bold ink">Verification pending</h3>
          <p className="mt-2 ink-soft text-sm">TrustDmarc is engineered as a defense-in-depth platform — every layer enforces explicit, auditable trust boundaries. Telemetry ingestion is disabled until DNS ownership is verified.</p>
          <pre className="mt-4 font-mono text-xs text-stone-300 bg-[#0F0E0C] rounded-lg p-5 overflow-x-auto">
{`Type:  TXT
Host:  ${d.verification_host}
Value: trustdmarc-verification=${d.verification_token}`}
          </pre>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, danger }) {
  return (
    <div className="panel p-5">
      <div className="text-xs font-medium uppercase tracking-wider ink-muted">{label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${danger ? "text-red-700" : "ink"}`}>{value}</div>
    </div>
  );
}
